import urlConstants from '~/lib/constants/url.constants';

import api from '../api';
import { CacheTag } from '../CacheTag';
import {
  CodeAndLabel,
  PrimaryDischargeStatusReason,
  SecondaryDischargeReason,
} from './refDischargePlan';
import { DischargePlanStatuses } from '~/components/maud/patients/profile/tabs/careplan/dischargeplan/common/dischargePlanConstants';

interface GetDischargePlanTasksRequest {
  dischargePlanId: string;
  pageSize?: number;
  pageNumber?: number;
}
export interface GetDischargePlanResponse {
  id: string;
  treatmentEpisodeId: string;
  status: CodeAndLabel;
  tentativeDischargeDate: string | null;
  whoInitiatedDischarge: CodeAndLabel | null;
  primaryDischargeReason: PrimaryDischargeStatusReason | null;
  secondaryDischargeReason: SecondaryDischargeReason | null;
  additionalDischargeReasonDetails: string | null;
  isPatientSeekingCare: CodeAndLabel | null;
  typeOfCare: CodeAndLabel[];
  levelOfCare: CodeAndLabel[];
  statusAtDischarge: string | null;
  aftercareBehavioral: string | null;
  aftercareMedical: string | null;
  aftercareDietary: string | null;
  aftercareOther: string | null;
  referralsAndFollowUpAppointments: string | null;
  signOffJustification: string | null;
  signedByUserId: string | null;
  signedAt: string | null;
}
export type PutDischargePlanDetailsResponse = Omit<
  GetDischargePlanResponse,
  | 'aftercareBehavioral'
  | 'aftercareMedical'
  | 'aftercareDietary'
  | 'aftercareOther'
  | 'referralsAndFollowUpAppointments'
  | 'signOffJustification'
  | 'signedByUserId'
  | 'signedAt'
>;

export interface PutDischargePlanDetailsRequest {
  additionalDischargeReasonDetails: string | null;
  isPatientSeekingCareCode: string | null;
  primaryDischargeReasonExternalId: string | null;
  secondaryDischargeReasonCode: string | null;
  typeOfCareCodes: string[];
  levelOfCareCodes: string[];
  statusAtDischarge: string | null;
  whoInitiatedDischargeCode: string | null;
  tentativeDischargeDate?: string | null;
}

export interface PatchDischargePlanRequest {
  aftercareBehavioral?: string | null;
  aftercareMedical?: string | null;
  aftercareDietary?: string | null;
  aftercareOther?: string | null;
  referralsAndFollowUpAppointments?: string | null;
}

export interface CreateDischargePlanTaskRequest {
  dischargePlanId: string;
  taskName: string;
  taskDescription?: string;
  taskCategoryExternalId: string;
  taskSubCategoryExternalId?: string;
  taskCreatedByUserId?: string;
  taskCreatedByUserName?: string;
  taskPriorityId: string;
  taskStatusId: string;
  taskAssignedUserId?: string;
  dueDate?: string;
}
interface UpdateDischargePlanStatusRequest {
  dischargePlanId: string;
  statusCode: string;
  signOffJustificationReason?: string | null;
  currentStatusCode?: string;
}

// All Discharge Plan API resources live under "/clinical-data/discharge-plans"
export const dischargePlanApi = api.injectEndpoints({
  endpoints: (build) => ({
    createDischargePlanTask: build.mutation<
      { taskExternalId: string },
      CreateDischargePlanTaskRequest
    >({
      invalidatesTags: (_result, _error, { dischargePlanId }) => [
        { id: dischargePlanId, type: CacheTag.DischargePlanTasks },
      ],
      query: ({ dischargePlanId, ...body }) => ({
        body,
        method: 'POST',
        url: urlConstants.clinical_data.discharge_plans.addTask(
          dischargePlanId,
        ),
      }),
    }),
    getDischargePlanById: build.query<GetDischargePlanResponse, string>({
      providesTags: (result, error, dischargePlanId) => [
        { id: dischargePlanId, type: CacheTag.DischargePlan },
      ],
      query: (dischargePlanId) => ({
        method: 'GET',
        url: urlConstants.clinical_data.discharge_plans.getById(
          dischargePlanId,
        ),
      }),
    }),
    getDischargePlanTasks: build.query<
      TaskSummaryResponse,
      GetDischargePlanTasksRequest
    >({
      providesTags: (_result, _error, { dischargePlanId }) => [
        { id: dischargePlanId, type: CacheTag.DischargePlanTasks },
      ],
      query: ({ dischargePlanId, pageSize, pageNumber }) => {
        const params: Record<string, number> = {};
        if (pageSize !== undefined) {
          params['page-size'] = pageSize;
        }
        if (pageNumber !== undefined) {
          params['page-number'] = pageNumber;
        }
        return {
          method: 'GET',
          url: urlConstants.clinical_data.discharge_plans.getTasks(
            dischargePlanId,
          ),
          params,
        };
      },
    }),
    updateDischargePlan: build.mutation<
      DischargePlanIdResponse,
      {
        dischargePlanId: string;
        dischargePlanPayload: PatchDischargePlanRequest;
      }
    >({
      invalidatesTags: (result, error, { dischargePlanId }) => [
        { id: dischargePlanId, type: CacheTag.DischargePlan },
      ],
      query: ({ dischargePlanId, dischargePlanPayload }) => ({
        body: dischargePlanPayload,
        method: 'PATCH',
        url: urlConstants.clinical_data.discharge_plans.getById(
          dischargePlanId,
        ),
      }),
    }),
    updateDischargePlanDetails: build.mutation<
      PutDischargePlanDetailsResponse,
      {
        dischargePlanId: string;
        dischargePlanDetails: PutDischargePlanDetailsRequest;
      }
    >({
      invalidatesTags: (result, error, { dischargePlanId }) => [
        { id: dischargePlanId, type: CacheTag.DischargePlan },
        { id: dischargePlanId, type: CacheTag.DischargePlanTasks },
      ],
      query: ({ dischargePlanId, dischargePlanDetails }) => ({
        body: dischargePlanDetails,
        method: 'PUT',
        url: urlConstants.clinical_data.discharge_plans.updateDetails(
          dischargePlanId,
        ),
      }),
    }),
    updateDischargePlanStatus: build.mutation<
      DischargePlanIdResponse,
      UpdateDischargePlanStatusRequest
    >({
      invalidatesTags: (
        result,
        error,
        { dischargePlanId, statusCode, currentStatusCode },
      ) => {
        const tags = [{ id: dischargePlanId, type: CacheTag.DischargePlan }];
        if (
          statusCode === DischargePlanStatuses.PREPPING.code &&
          currentStatusCode === DischargePlanStatuses.NOT_STARTED.code
        ) {
          tags.push({ id: dischargePlanId, type: CacheTag.DischargePlanTasks });
        }
        return tags;
      },

      query: ({ dischargePlanId, statusCode, signOffJustificationReason }) => ({
        body: {
          statusCode,
          signOffJustificationReason,
        },
        method: 'PATCH',
        url: urlConstants.clinical_data.discharge_plans.updateStatus(
          dischargePlanId,
        ),
      }),
    }),
  }),
});

export const {
  useCreateDischargePlanTaskMutation,
  useGetDischargePlanByIdQuery,
  useGetDischargePlanTasksQuery,
  useUpdateDischargePlanMutation,
  useUpdateDischargePlanDetailsMutation,
  useUpdateDischargePlanStatusMutation,
} = dischargePlanApi;
